import type {
  RoutineTimelineEntry,
  RoutineTimelineProjectionRoutine,
  RoutineTimelineRoutine,
} from "@rakazo/contracts";
import { TIMELINE_MAX_ENTRIES } from "@rakazo/contracts";
import { createNextCronDateAcrossStrict, isOneShotRoutineCrons } from "./cron.js";

export {
  TIMELINE_MAX_BOTS,
  TIMELINE_MAX_ENTRIES,
  TIMELINE_MAX_ROUTINES,
  TIMELINE_MAX_SPACES,
} from "@rakazo/contracts";

export function calendarDayAt(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

export function shiftCalendarDay(day: string, amount: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== day)
    throw new RangeError("Invalid calendar day / Некоректний календарний день");
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

/** Find local midnight by calendar date, including 23/25-hour days and midnight shifts.
 * Знаходимо місцеву опівніч за календарем, включно з днями 23/25 годин і зміщенням опівночі. */
export function calendarDayInterval(day: string, timezone: string): { from: Date; to: Date } {
  const next = shiftCalendarDay(day, 1);
  const midnight = (target: string) => {
    const center = new Date(`${target}T00:00:00Z`).getTime();
    let low = center - 36 * 3_600_000;
    let high = center + 36 * 3_600_000;
    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (calendarDayAt(new Date(middle), timezone) < target) low = middle + 1;
      else high = middle;
    }
    return new Date(low);
  };
  const from = midnight(day);
  const to = midnight(next);
  if (from >= to || calendarDayAt(from, timezone) !== day)
    throw new RangeError("Calendar day does not exist / Календарного дня не існує");
  return { from, to };
}

/** Only project future starts; past facts come from stored Runs, never today's edited rules.
 * Обчислюємо лише майбутні старти; минуле походить зі збережених запусків, не нових правил. */
export function projectRoutineTimeline(
  routines: RoutineTimelineProjectionRoutine[],
  from: Date,
  to: Date,
  now: Date,
  limit = TIMELINE_MAX_ENTRIES,
): { entries: RoutineTimelineEntry[]; partial: boolean } {
  const entries: RoutineTimelineEntry[] = [];
  let partial = false;
  let work = 0;
  const scheduleCounts = new Map<string, number>();
  for (const routine of routines) {
    const signature = JSON.stringify([routine.timezone, routine.crons]);
    scheduleCounts.set(signature, (scheduleCounts.get(signature) ?? 0) + 1);
  }
  // Retain timestamps only; parsed schedules stay local to each routine.
  // Зберігаємо лише часові позначки; розібрані правила локальні для кожного завдання.
  const nextDatesBySchedule = new Map<string, Map<number, number | null>>();
  const floor = new Date(Math.max(from.getTime(), now.getTime()));
  for (const routine of routines) {
    if (!routine.crons.length || floor >= to) continue;
    if (routine.crons.length > 32) {
      partial = true;
      continue;
    }
    const add = (at: Date) => {
      if (entries.length >= limit) return false;
      entries.push({
        key: `${routine.id}:${at.toISOString()}`,
        routineId: routine.id,
        originId: routine.id,
        botId: routine.botId,
        spaceId: routine.spaceId,
        name: routine.name,
        at: at.toISOString(),
        scheduledAt: at.toISOString(),
        skipReason: null,
        status: routine.active ? "planned" : "paused",
        runId: null,
        startedAt: null,
        completedAt: null,
        executionWaitReason: null,
      });
      return true;
    };
    if (isOneShotRoutineCrons(routine.crons)) {
      const at = routine.nextRunAt ?? routine.scheduledOnceAt;
      if (at && !routine.lastRunAt) {
        const date = new Date(at);
        if (date >= floor && date < to && !add(date)) return { entries, partial: true };
      }
    } else {
      let nextDate: (from: Date) => Date | null;
      try {
        const signature = JSON.stringify([routine.timezone, routine.crons]);
        if (
          (scheduleCounts.get(signature) ?? 0) < 2 ||
          (!nextDatesBySchedule.has(signature) && nextDatesBySchedule.size >= 32)
        ) {
          nextDate = createNextCronDateAcrossStrict(routine.crons, routine.timezone);
        } else {
          let values = nextDatesBySchedule.get(signature);
          let calculate: ((from: Date) => Date | null) | undefined;
          if (!values || values.size === 0) {
            calculate = createNextCronDateAcrossStrict(routine.crons, routine.timezone);
            if (!values) {
              values = new Map();
              nextDatesBySchedule.set(signature, values);
            }
          }
          const timestamps = values;
          nextDate = (cursor) => {
            const stamp = cursor.getTime();
            const cached = timestamps.get(stamp);
            if (cached !== undefined) return cached === null ? null : new Date(cached);
            calculate ??= createNextCronDateAcrossStrict(routine.crons, routine.timezone);
            const next = calculate(cursor);
            timestamps.set(stamp, next?.getTime() ?? null);
            return next;
          };
        }
      } catch {
        partial = true;
        continue;
      }
      let cursor = new Date(Math.max(floor.getTime(), new Date(routine.createdAt).getTime()) - 1);
      while (true) {
        work += routine.crons.length;
        if (work > 10_000) return { entries, partial: true };
        let next: Date | null;
        try {
          next = nextDate(cursor);
        } catch {
          partial = true;
          break;
        }
        if (!next || next >= to) break;
        if (next <= cursor) {
          partial = true;
          break;
        }
        if (!add(next)) return { entries, partial: true };
        cursor = next;
      }
    }
  }
  return { entries, partial };
}

export type RoutineTimelineGroup = {
  entries: RoutineTimelineEntry[];
  from: number;
  to: number;
  at: number;
};

/** Calendar-hour membership is independent of pixels; repeated offsets stay separate.
 * Належність до календарної години незалежна від пікселів; повторні зміщення окремі. */
export function groupTimelineEntries(
  entries: RoutineTimelineEntry[],
  timezone = "UTC",
): RoutineTimelineGroup[] {
  if (!entries.length) return [];
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "longOffset",
  });
  const calendar = (stamp: number) => {
    const parts = formatter.formatToParts(new Date(stamp));
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    return {
      key: `${get("year")}-${get("month")}-${get("day")}T${get("hour")}|${get("timeZoneName")}`,
      minute: Number(get("minute")),
      second: Number(get("second")),
    };
  };
  const groups = new Map<string, RoutineTimelineGroup>();
  for (const entry of [...entries].sort(
    (a, b) => Date.parse(a.at) - Date.parse(b.at) || a.key.localeCompare(b.key),
  )) {
    const stamp = Date.parse(entry.at);
    const local = calendar(stamp);
    const existing = groups.get(local.key);
    if (existing) {
      existing.entries.push(entry);
      existing.to = stamp;
      continue;
    }
    let at =
      stamp - local.minute * 60_000 - local.second * 1000 - new Date(stamp).getMilliseconds();
    // A non-whole-hour DST transition can make the nominal hour start nonexistent.
    // Неповногодинна зміна часу може зробити номінальний початок години неіснуючим.
    if (calendar(at).key !== local.key) {
      let low = at,
        high = stamp;
      while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (calendar(middle).key === local.key) high = middle;
        else low = middle + 1;
      }
      at = low;
    }
    groups.set(local.key, { entries: [entry], from: stamp, to: stamp, at });
  }
  return [...groups.values()].map((group) =>
    group.entries.length === 1 ? { ...group, at: group.from } : group,
  );
}

/** Pack label collisions into subrows without changing hour membership or time coordinates.
 * Розносимо підписи на підрядки, не змінюючи годинні групи й часові координати. */
export function layoutTimelineGroups(
  groups: RoutineTimelineGroup[],
  start: number,
  pixelsPerMs: number,
  footprint = 184,
) {
  const rowEnds: number[] = [];
  return groups.map((group) => {
    const center = (group.at - start) * pixelsPerMs;
    let row = rowEnds.findIndex((right) => right <= center - footprint / 2);
    if (row < 0) row = rowEnds.length;
    rowEnds[row] = center + footprint / 2;
    return { ...group, row };
  });
}

export type RoutineTimelineState = RoutineTimelineEntry["status"] | "task-active" | "task-paused";

/** Run facts and the current task state are distinct; omitted metadata stays unknown.
 * Факти запуску й поточний стан завдання окремі; відсутні відомості лишаються невідомими. */
export function routineTimelineStates(
  entries: RoutineTimelineEntry[],
  routines: ReadonlyMap<string, RoutineTimelineRoutine>,
) {
  const states = new Map<string, { state: RoutineTimelineState; entry: RoutineTimelineEntry }>();
  for (const entry of entries) {
    const active = entry.routineId ? routines.get(entry.routineId)?.active : undefined;
    const future = entry.status === "planned" || entry.status === "paused";
    const taskState = active === undefined ? null : active ? "task-active" : "task-paused";
    const state = future && taskState ? taskState : entry.status;
    states.set(`${state}:${entry.executionWaitReason ?? ""}`, { state, entry });
    if (!future && taskState) states.set(`${taskState}:`, { state: taskState, entry });
  }
  return [...states.values()];
}

export function routineTimelineStateStyle(state: RoutineTimelineState) {
  if (state === "completed" || state === "task-active")
    return { tone: "success", symbol: state === "completed" ? "✓" : "▶" } as const;
  if (state === "failed") return { tone: "destructive", symbol: "!" } as const;
  if (state === "paused" || state === "task-paused")
    return { tone: "warning", symbol: "Ⅱ" } as const;
  if (state === "cancelled") return { tone: "muted", symbol: "×" } as const;
  if (state === "skipped") return { tone: "warning", symbol: "↷" } as const;
  if (["queued", "leased", "waiting_input", "waiting_takeover"].includes(state))
    return { tone: "warning", symbol: "◷" } as const;
  return { tone: "foreground", symbol: "○" } as const;
}
